/* Spytec paid acquisition evidence (ChatGPT ads). No purchase events or revenue changes. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.start(root, root.document);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const KEY = 'spytec_chatgpt_touch_v1';
  const TTL = 30 * 86400000;
  const FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content', 'oppref'];
  const ATTRS = ['__spytec_paid_first', '__spytec_paid_last', '__spytec_paid_schema'];
  const SOURCES = ['chatgpt', 'chatgpt.com', 'openai'];
  const PAID_MEDIUMS = ['cpc', 'ppc', 'paid'];
  const EVIDENCE = ['paid_utm', 'oppref'];
  function clean(value) {
    return String(value || '').replace(/[\x00-\x1f\x7f]/g, '').slice(0, 200);
  }
  // Unexpanded ad-platform macros ("{oppref}") are not real values.
  function genuine(value) {
    return Boolean(value) && !/[{}]/.test(value);
  }
  function landing(href, now) {
    const url = new URL(href);
    const source = (url.searchParams.get('utm_source') || '').toLowerCase();
    const medium = (url.searchParams.get('utm_medium') || '').toLowerCase();
    const paidUtm = SOURCES.includes(source) && PAID_MEDIUMS.includes(medium);
    // oppref is OpenAI's ad click reference, so it is paid evidence on its own.
    const oppref = genuine(clean(url.searchParams.get('oppref')));
    // Organic ChatGPT referrals (utm_source=chatgpt.com, no paid medium) stay out.
    if (!paidUtm && !oppref) return null;
    const touch = { at: now, path: url.pathname, evidence: paidUtm ? 'paid_utm' : 'oppref' };
    // Keep existing product destinations; only explicit paid tags create evidence.
    FIELDS.forEach(field => {
      const value = clean(url.searchParams.get(field));
      if (genuine(value)) touch[field] = value;
    });
    return touch;
  }
  function validTouch(touch, now) {
    return touch && Number.isFinite(touch.at) && touch.at <= now && now - touch.at < TTL &&
      EVIDENCE.includes(touch.evidence);
  }
  function reconcile(stored, touch, now) {
    let state = stored && validTouch(stored.last, now) ? stored : null;
    if (state && !validTouch(state.first, now)) state = { first: state.last, last: state.last };
    if (!touch) return state;
    // Same URL on refresh must not extend the attribution window indefinitely.
    const identity = t => JSON.stringify(Object.fromEntries(Object.entries(t).filter(([k]) => k !== 'at')));
    if (state && identity(state.last) === identity(touch)) return state;
    return { first: state ? state.first : touch, last: touch };
  }
  function attributes(state) {
    return {
      [ATTRS[0]]: state ? JSON.stringify(state.first) : '',
      [ATTRS[1]]: state ? JSON.stringify(state.last) : '',
      [ATTRS[2]]: state ? '1' : ''
    };
  }
  function start(win, doc) {
    let current = null;
    let pending = Promise.resolve();
    let lastSent = null;
    let active = false;
    let retry = 0;
    function read() {
      try { return JSON.parse(win.localStorage.getItem(KEY)); } catch (_) { return null; }
    }
    function save(value) {
      try {
        if (value) win.localStorage.setItem(KEY, JSON.stringify(value));
        else win.localStorage.removeItem(KEY);
      } catch (_) { /* Private browsing must not interrupt purchasing. */ }
    }
    function send(value) {
      const body = JSON.stringify({ attributes: attributes(value) });
      if (body === lastSent) return;
      lastSent = body;
      const base = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
      // Serialize only our updates; never replace other cart attributes or items.
      pending = pending.then(() => win.fetch(base + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body
      })).then(response => {
        if (!response.ok) throw new Error('Attribution write failed');
      }).catch(() => { lastSent = null; });
    }
    function consentAllowed() {
      try { return win.Shopify.customerPrivacy.marketingAllowed() === true; }
      catch (_) { return false; }
    }
    function update() {
      if (!win.Shopify || !win.Shopify.customerPrivacy) return;
      const old = read();
      const hadTouch = old || current;
      active = consentAllowed();
      if (!active) {
        current = null;
        save(null);
        if (hadTouch) send(null);
        return;
      }
      const now = Date.now();
      current = reconcile(old || current, landing(win.location.href, now), now);
      save(current);
      if (current || old) send(current);
    }
    // Existing product forms construct FormData on submit. These private line
    // properties provide an order-level fallback if the cart request is delayed.
    doc.addEventListener('submit', function (event) {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      let action;
      try { action = new URL(form.action, win.location.href); } catch (_) { return; }
      if (action.origin !== win.location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
      update();
      ['first', 'last'].forEach(which => {
        const name = 'properties[_spytec_paid_' + which + ']';
        let input = form.querySelector('input[name="' + name + '"]');
        if (!active || !current) { if (input) input.remove(); return; }
        if (!input) {
          input = doc.createElement('input'); input.type = 'hidden'; input.name = name;
          form.appendChild(input);
        }
        input.value = JSON.stringify(current[which]);
      });
    }, true);
    doc.addEventListener('visitorConsentCollected', update);
    win.addEventListener('pageshow', update);
    function initialize() {
      if (win.Shopify && win.Shopify.customerPrivacy) { update(); return; }
      if (win.Shopify && typeof win.Shopify.loadFeatures === 'function') {
        win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
          if (!error) update();
        });
      } else if (retry++ < 10) win.setTimeout(initialize, 500);
    }
    initialize();
  }
  return { landing, reconcile, attributes, validTouch, start, TTL, KEY };
});
